<template>
  <div
    class="lego-tree-dropdown lego-dropdown lego-input"
    :class="dropdownClass"
    :style="[dropdownWidth, dropdownHeight]"
    v-dropdown-click-outside="toggleItemOuter"
    ref="scpmTreeDropdown"
  >
    <div
      class="lego-dropdown__title"
      :class="{ 'lego-dropdown--focused': isFocused }"
      :tabindex="autoComplete || disabled ? -1 : 0"
      @click="toggleItem"
      @keydown.stop="onKeydown"
    >
      <div
        class="lego-dropdown__selected"
        :style="dropdownTitleStyle"
        v-lego-tooltip:ellipsis="getText(selItem)"
      >
        <span>
          <!--
                $ver: v0.1.2
                드랍다운 항목 내부에 위치하는 영역 입니다.
                #This area is located inside the dropdown item.
            -->
          <slot name="selectedLabel" :text="getText(selItem)"></slot>
          <slot
            v-if="!$slots['selectedLabel']"
            name="itemText"
            :item="selItem"
            :getText="getText"
            >{{ getText(selItem) }}</slot
          >
        </span>
      </div>
      <lego-icon-wrapper class="lego-dropdown__title-icon">
        <lego-icon
          class="lego-dropdown__icon"
          icon-type="df"
          icon="arrow_down"
          size="16px"
          :style="caretStyle"
        />
      </lego-icon-wrapper>
    </div>
    <div class="lego-dropdown-menu" v-if="isItemShowing && items.length === 0">
      <div class="no-data">{{ t('label.noData') }}</div>
    </div>
    <div
      class="lego-dropdown__items"
      v-if="isItemShowing && items.length > 0"
      :class="itemsClass"
      ref="lego-dropdown__items"
    >
      <c-scroll
        ref="scrollRef"
        :style="maxHeightScroll"
        style="background-color: white; border: 1px solid #364fc7"
      >
        <lego-accordion-item
          v-for="(item, index) in items"
          :title="item.text"
          :key="index"
          :index="isExpand && isExpand.toLowerCase() == item.value.toLowerCase() ? 0 : 1"
          :expand="isExpand && isExpand.toLowerCase() == item.value.toLowerCase()"
          expandable
        >
          {{ item.value }}
          <template #title>
            <slot name="title" :item="item">
              <div class="lego-tree-dropdown__title">
                <img :src="item.icon" width="15" />
                <strong
                  >{{ item.text }} ({{
                    item.childItems && item.childItems.length > 0 ? item.childItems.length : 0
                  }})</strong
                >
              </div>
            </slot>
          </template>
          <template #expand>
            <slot name="expand" :item="item">
              <div
                class="lego-dropdown__item"
                :class="{
                  'lego-dropdown__item--focused': idx === focusedItemIndex,
                  'lego-dropdown__item--active': isSelectedItem(childItem),
                  'lego-dropdown__item--sub': childItem.isSub
                }"
                v-for="(childItem, idx) in item.childItems"
                @click.stop="clickItem({ ...childItem, item })"
                :key="idx"
              >
                <ScpmSubTreeDropdown
                  v-if="childItem.isSub"
                  :item="item"
                  :items="childItem.childItems"
                  :title="childItem.text"
                  @click="clickItem"
                  v-model="modelValue"
                  ref="dropdownSub"
                />
                <div
                  v-else
                  class="lego-dropdown__label"
                  :class="{ 'lego-dropdown__selected': isSelectedItem(childItem) }"
                  :id="`dropdown-child-${childItem.typeOrder}`"
                >
                  <span v-lego-tooltip:ellipsis="getText(childItem)" class="ellipsis">
                    <slot name="itemText" :item="childItem" :getText="getText">{{
                      getText(childItem)
                    }}</slot>
                  </span>
                </div>
              </div>
            </slot>
          </template>
        </lego-accordion-item>
      </c-scroll>
    </div>
    <div
      v-if="messageShow"
      class="lego-dropdown__message lego-validator__message"
      :class="messageClass"
      :style="customValidationStyle"
    >
      {{ messageShow }}
    </div>
  </div>
</template>

<script>
import i18nMixin from '@/mixins/i18n.mixin.js';
import legoDropdown from '@/lego-component/components/element/legoDropdown/legoDropdown.vue';
import cScroll from '../../cmp/cScroll/cScroll.vue';
import ScpmSubTreeDropdown from './ScpmSubTreeDropdown';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';

export default {
  mixins: [i18nMixin],
  name: 'scpm-tree-dropdown',
  components: { cScroll, ScpmSubTreeDropdown },
  emits: ['getIcon', 'update:modelValue'],
  extends: legoDropdown,
  props: {
    setMaxHeightScroll: {
      type: [Number, String],
      default: 310
    }
  },
  mounted() {
    legoEventBus.$on('close_tooltips', this.toggleItemOuter);
    legoEventBus.$on('click-outside', this.toggleItemOuter);
  },
  beforeUnmount() {
    legoEventBus.$off('close_tooltips', this.toggleItemOuter);
    legoEventBus.$off('click-outside', this.toggleItemOuter);
  },
  computed: {
    maxHeightScroll() {
      return `max-height: ${this.setMaxHeightScroll}px`;
    },
    isExpand() {
      if (this.selItem) {
        return this.selItem.groupName || '';
      }
      return this.items[0].value;
    }
  },
  methods: {
    toggleItem() {
      this.isItemShowing = !this.isItemShowing;

      if (this.isItemShowing) {
        this.$nextTick(() => {
          const ref = this.$refs.scpmTreeDropdown;
          let element = null;
          if (this.$refs.scpmTreeDropdown && this.selItem) {
            element = ref.querySelector(`#dropdown-child-${this.selItem.typeOrder}`);
          }

          if (element) {
            const scrollBar = this.$refs[`lego-dropdown__items`].querySelector(
              '.simplebar-content-wrapper'
            );

            const dropdownChildRect = element.getBoundingClientRect();
            const scrollbarRect = scrollBar.getBoundingClientRect();

            const distance = dropdownChildRect.top - scrollbarRect.top;

            if (distance > scrollbarRect.height) {
              scrollBar.scrollTo({
                top: distance,
                behavior: 'smooth'
              });
            }
          }
        });
      }
    },
    clickItem(item) {
      if (item.isSub) return;
      this.$emit('getIcon', item);
      this.$emit('update:modelValue', this.getValue(item));
      if (this.autoComplete) {
        this.toggleItemOuter();
      } else {
        this.toggleItem();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
:deep .simplebar-content {
  border: none !important;
}
.lego-dropdown {
  &__title {
    border-radius: 2px;

    &:focus {
      border: $lego-dropdown__title__border;
    }

    &:hover {
      border-color: $lego-dropdown__title__border--focus;
    }
  }
}

.lego-dropdown-menu {
  position: absolute;
  border: 1px solid #364fc7;
  min-width: 100%;
  margin-top: 0px;
  background-color: #ffffff;
  z-index: 200;
  cursor: default;
  padding: 25px 0;
  text-align: center;
  font-size: 11px;
  color: #767676;
}

.accordion-list-item {
  margin-top: 0px !important;
}

.lego-tree-dropdown {
  &__title {
    display: flex;
    align-items: center;

    strong {
      display: block;
      margin-left: 8px;
      font-weight: 500;
    }
  }

  .lego-dropdown__items {
    .lego-dropdown__item {
      padding: 0;

      &--active {
        background-color: rgba(54, 79, 199, 0.12);
      }

      &--sub {
        background-color: #ffffff;
      }
    }

    .lego-dropdown {
      &__selected {
        font-weight: 500;
      }

      &__label {
        min-height: 30px;
        padding-left: 31px;
      }
    }

    border: none !important;
  }

  :deep .accordion-list-item {
    border: none;

    &__main {
      display: flex;
      justify-content: space-between;
      padding: 0 0 0 8px !important;
    }

    &__expand {
      border-top: 0 !important;
      margin: 0 !important;
      padding: 0;
    }
  }

  .lego-dropdown__item--sub {
    :deep .accordion-list-item {
      &__main {
        padding-left: 31px !important;
      }
    }
  }
}
</style>
